import { Slot } from '@radix-ui/react-slot';
import { useLocalStorage } from '@uidotdev/usehooks';
import * as React from 'react';
import { NavLink, useMatch } from 'react-router-dom';
import { useMedia } from 'react-use';

import { EluveNameLogo } from './EluveNameLogo';
import { HStack } from './HStack';
import { Icon, IconProps } from './Icon';
import { legacyTextStyles } from './LegacyTextStyles';
import { NewButton } from './NewButton';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { VStack } from './VStack';
import { VariantProps, cn, tv } from './cn';
import { Input } from './input';
import { Separator } from './separator';
import { Sheet, SheetContent } from './sheet';
import { Skeleton } from './skeleton';

const SIDEBAR_LOCAL_STORAGE_KEY = 'sidebar-open-state';
const SIDEBAR_WIDTH = '15rem';
const SIDEBAR_WIDTH_MOBILE = '15rem';
const SIDEBAR_WIDTH_ICON = '3.25rem';

type SidebarContext = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  hovered: boolean;
  setHovered: (hovered: boolean) => void;
  canBeExpanded: boolean;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const canBeExpanded = useMedia('(min-width: 80rem)');
    const isMobile = !useMedia('(min-width: 48rem)');

    const [openMobile, setOpenMobile] = React.useState(false);
    const [storedOpenState, setStoredOpenState] = useLocalStorage(
      SIDEBAR_LOCAL_STORAGE_KEY,
      defaultOpen,
    );

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(storedOpenState);
    const open = openProp ?? _open;
    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        if (!canBeExpanded) {
          return;
        }

        const openState = typeof value === 'function' ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }

        // This updates the local storage to keep the sidebar state.
        setStoredOpenState(openState);
      },
      [canBeExpanded, open, setOpenProp, setStoredOpenState],
    );

    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(() => {
      return isMobile
        ? setOpenMobile((open) => !open)
        : setOpen((open) => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    // Handling hover state of the sidebar.
    const [hovered, setHovered] = React.useState(false);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = canBeExpanded && open ? 'expanded' : 'collapsed';

    const contextValue = React.useMemo<SidebarContext>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        hovered,
        setHovered,
        canBeExpanded,
      }),
      [
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        hovered,
        setHovered,
        canBeExpanded,
      ],
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <Slot
          children={children}
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn('group/sidebar-wrapper', className)}
          ref={ref}
          {...props}
        />
      </SidebarContext.Provider>
    );
  },
);
SidebarProvider.displayName = 'SidebarProvider';

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    side?: 'left' | 'right';
    variant?: 'sidebar' | 'floating' | 'inset';
    collapsible?: 'offcanvas' | 'icon' | 'none';
  }
>(
  (
    {
      side = 'left',
      variant = 'inset',
      collapsible = 'icon',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { isMobile, state, openMobile, setOpenMobile, setHovered } =
      useSidebar();

    if (collapsible === 'none') {
      return (
        <div
          className={cn(
            'flex h-full w-[--sidebar-width] flex-col text-text-tertiary',
            className,
          )}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            className="w-[--sidebar-width] p-0 text-text-tertiary"
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
            side={side}
          >
            <div className="flex size-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <div
        ref={ref}
        className="group peer hidden text-text-tertiary md:block"
        data-state={state}
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-variant={variant}
        data-side={side}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            'relative h-svh w-[--sidebar-width] bg-transparent transition-[width] duration-200 ease-linear',
            'group-data-[collapsible=offcanvas]:w-0',
            'group-data-[side=right]:rotate-180',
            variant === 'floating' || variant === 'inset'
              ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]'
              : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon]',
          )}
        />
        <div
          className={cn(
            'fixed inset-y-0 z-10 hidden h-svh w-[--sidebar-width] transition-[left,right,width] duration-200 ease-linear md:flex',
            side === 'left'
              ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
              : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
            // Adjust the padding for floating and inset variants.
            variant === 'floating' || variant === 'inset'
              ? 'p-1.5 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+0px)]'
              : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l',
            className,
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="flex size-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-border-secondary group-data-[variant=floating]:shadow"
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);
Sidebar.displayName = 'Sidebar';

function SidebarTrigger() {
  const { isMobile, toggleSidebar } = useSidebar();

  if (!isMobile) {
    return null;
  }

  return (
    <NewButton icon={{ name: 'Menu' }} onClick={toggleSidebar} type="ghost" />
  );
}

const SidebarRail = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'>
>(({ className, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        'absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-border-secondary group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex',
        '[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      {...props}
    />
  );
});
SidebarRail.displayName = 'SidebarRail';

const SidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        'h-8 w-full bg-bg-primary shadow-none focus-visible:ring-2 focus-visible:ring-border-accent-quaternary',
        className,
      )}
      {...props}
    />
  );
});
SidebarInput.displayName = 'SidebarInput';

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn(
        'flex flex-col items-start justify-start gap-1.5 p-1.5',
        className,
      )}
      {...props}
    />
  );
});
SidebarHeader.displayName = 'SidebarHeader';

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn('flex flex-col gap-1.5 p-1.5', className)}
      {...props}
    />
  );
});
SidebarFooter.displayName = 'SidebarFooter';

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn('mx-2 w-auto bg-border-secondary', className)}
      {...props}
    />
  );
});
SidebarSeparator.displayName = 'SidebarSeparator';

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-1.5 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className,
      )}
      {...props}
    />
  );
});
SidebarContent.displayName = 'SidebarContent';

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col p-1.5', className)}
      {...props}
    />
  );
});
SidebarGroup.displayName = 'SidebarGroup';

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        'flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-text-tertiary/70 outline-none ring-border-accent-quaternary transition-[margin,opa] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
        className,
      )}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        'absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-text-tertiary outline-none ring-border-accent-quaternary transition-transform hover:bg-bg-tertiary hover:text-text-tertiary focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 after:md:hidden',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
});
SidebarGroupAction.displayName = 'SidebarGroupAction';

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn('w-full text-sm', className)}
    {...props}
  />
));
SidebarGroupContent.displayName = 'SidebarGroupContent';

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'> & { compact?: boolean }
>(({ className, compact, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn(
      'flex w-full min-w-0 flex-col gap-2',
      compact && 'gap-0',
      className,
    )}
    {...props}
  />
));
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    data-sidebar="menu-item"
    className={cn('group/menu-item relative', className)}
    {...props}
  />
));
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuCoreButtonVariants = tv({
  base: 'peer/menu-button flex w-full items-center gap-3 overflow-hidden rounded-md p-3 text-left text-sm outline-none ring-border-accent-quaternary transition-[width,height,padding] hover:bg-bg-tertiary hover:text-text-tertiary focus-visible:ring-2 active:bg-bg-quaternary active:text-text-tertiary disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-bg-quaternary data-[active=true]:font-medium data-[active=true]:text-text-primary data-[state=open]:hover:bg-bg-tertiary data-[state=open]:hover:text-text-tertiary group-data-[collapsible=icon]:!size-11 group-data-[collapsible=icon]:!p-3 [&>span:last-child]:truncate [&>svg]:size-5 [&>svg]:shrink-0',
  variants: {
    size: {
      default: legacyTextStyles.body({
        color: 'inherit',
        size: 'm',
        className: 'h-11',
      }),
      lg: legacyTextStyles.body({
        color: 'inherit',
        size: 'm',
        className: 'h-15 p-2 group-data-[collapsible=icon]:!p-0',
      }),
    },
    variant: {
      default: '',
      compact:
        'h-auto gap-2 p-2 [&>svg]:hidden group-data-[state=collapsed]:[&>svg]:block',
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
});

type SidebarMenuCoreButtonProps = VariantProps<
  typeof sidebarMenuCoreButtonVariants
>;

const SidebarMenuCoreButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & SidebarMenuCoreButtonProps
>(
  (
    {
      asChild = false,
      isActive = false,
      size,
      variant,
      tooltip,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useSidebar();

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(
          sidebarMenuCoreButtonVariants({ size, variant }),
          className,
        )}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === 'string') {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={state !== 'collapsed' || isMobile}
          {...tooltip}
        />
      </Tooltip>
    );
  },
);
SidebarMenuCoreButton.displayName = 'SidebarMenuCoreButton';

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        'absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-text-tertiary outline-none ring-border-accent-quaternary transition-transform hover:bg-bg-tertiary hover:text-text-tertiary focus-visible:ring-2 peer-hover/menu-button:text-text-tertiary [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 after:md:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-text-tertiary md:opacity-0',
        className,
      )}
      {...props}
    />
  );
});
SidebarMenuAction.displayName = 'SidebarMenuAction';

const SidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      'pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-text-tertiary',
      'peer-hover/menu-button:text-text-tertiary peer-data-[active=true]/menu-button:text-text-tertiary',
      'peer-data-[size=sm]/menu-button:top-1',
      'peer-data-[size=default]/menu-button:top-1.5',
      'peer-data-[size=lg]/menu-button:top-2.5',
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
));
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    showIcon?: boolean;
  }
>(({ className, showIcon = false, ...props }, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-[--skeleton-width] flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
});
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

const SidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-border-secondary px-2.5 py-0.5',
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
));
SidebarMenuSub.displayName = 'SidebarMenuSub';

const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ ...props }, ref) => <li ref={ref} {...props} />);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<'a'> & {
    asChild?: boolean;
    size?: 'sm' | 'md';
    isActive?: boolean;
  }
>(({ asChild = false, size = 'md', isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-text-tertiary outline-none ring-border-accent-quaternary hover:bg-bg-tertiary hover:text-text-tertiary focus-visible:ring-2 active:bg-bg-tertiary active:text-text-tertiary disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-text-tertiary',
        'data-[active=true]:bg-bg-tertiary data-[active=true]:text-text-tertiary',
        size === 'sm' && 'text-xs',
        size === 'md' && 'text-sm',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

// Custom Components

function SidebarHeaderTrigger() {
  const {
    hovered: isHovered,
    isMobile,
    open: isOpen,
    toggleSidebar,
    state,
  } = useSidebar();
  const isCollapsed = state !== 'expanded';

  if (isMobile) {
    return null;
  }

  const button = (
    <button
      data-sidebar="trigger"
      className={sidebarMenuCoreButtonVariants({
        className: cn(
          '!size-11 self-center opacity-0 transition-opacity duration-200 ease-linear',
          isCollapsed && 'self-start',
          (isCollapsed || isHovered) && 'opacity-100',
        ),
      })}
      onClick={() => {
        toggleSidebar();
      }}
    >
      <Icon name="Menu" size="sm" />
      <span className="sr-only">Toggle Sidebar</span>
    </button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" align="center" hidden={isOpen}>
        Toggle Sidebar
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarLogoHeader({ onClick }: { onClick?: () => void }) {
  const { canBeExpanded, isMobile, state } = useSidebar();
  const hasOnClick = Boolean(onClick);
  const isOpen = state === 'expanded';
  const isCollapsed = !isOpen && !isMobile;

  return (
    <HStack
      align="start"
      className={cn(
        'h-20 py-1 pl-0 transition-[height] duration-200 ease-linear',
        isCollapsed && 'flex-col pb-0',
        isCollapsed && canBeExpanded && 'h-[132px]',
      )}
      justify="between"
    >
      <VStack
        className={cn(
          'my-3.5 h-11 shrink-0 rounded-lg px-3.5 transition-[background] duration-200 ease-linear',
          hasOnClick && 'cursor-pointer',
          isCollapsed && 'bg-text-primary',
        )}
        justify="center"
        onClick={onClick}
        wFull={false}
      >
        <div
          className={cn(
            '-mt-0.5 w-[88px] overflow-hidden transition-[color,width] duration-200 ease-linear',
            isCollapsed && 'w-4',
          )}
        >
          <EluveNameLogo
            className={cn(
              'transition-[fill] duration-200 ease-linear',
              isCollapsed && 'fill-bg-primary',
            )}
            height={42}
          />
        </div>
      </VStack>
      {canBeExpanded && <SidebarHeaderTrigger />}
    </HStack>
  );
}

function SidebarContentSpacer() {
  return <div className="flex-1" />;
}

function SidebarMenuNavButton({
  icon,
  isActive: isForcedActive,
  text,
  to,
  variant,
}: {
  icon: {
    name: IconProps['name'];
  };
  isActive?: boolean;
  text: string;
  to: string;
  variant?: SidebarMenuCoreButtonProps['variant'];
}) {
  const { isMobile, openMobile, setOpenMobile } = useSidebar();
  const match = useMatch(to);
  const isActive = Boolean(match);

  return (
    <SidebarMenuCoreButton
      asChild
      isActive={isActive || isForcedActive}
      onClick={() => {
        if (isMobile && openMobile) {
          setOpenMobile(false);
        }
      }}
      tooltip={text}
      variant={variant}
    >
      <NavLink to={to}>
        <Icon name={icon.name} size="sm" />
        <span>{text}</span>
      </NavLink>
    </SidebarMenuCoreButton>
  );
}

function SidebarMenuLinkButton({
  icon,
  href,
  rel = 'noreferrer',
  target = '_blank',
  text,
  variant,
}: {
  icon: {
    name: IconProps['name'];
  };
  href: string;
  rel?: string;
  target?: string;
  text: string;
  variant?: SidebarMenuCoreButtonProps['variant'];
}) {
  const { isMobile, openMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenuCoreButton
      asChild
      onClick={() => {
        if (isMobile && openMobile) {
          setOpenMobile(false);
        }
      }}
      tooltip={text}
      variant={variant}
    >
      <a href={href} rel={rel} target={target}>
        <Icon name={icon.name} size="sm" />
        <span>{text}</span>
      </a>
    </SidebarMenuCoreButton>
  );
}

/*
The following components are currently not used, so they are not exported:
  - SidebarGroupAction
  - SidebarGroupLabel
  - SidebarInput
  - SidebarMenuAction
  - SidebarMenuBadge
  - SidebarMenuSkeleton
  - SidebarMenuSub
  - SidebarMenuSubButton
  - SidebarMenuSubItem
  - SidebarRail
  - SidebarSeparator
*/

export {
  Sidebar,
  SidebarContent,
  SidebarContentSpacer,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarLogoHeader,
  SidebarMenu,
  SidebarMenuCoreButton,
  SidebarMenuItem,
  SidebarMenuLinkButton,
  SidebarMenuNavButton,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
};
